import React, { useEffect, useRef, useState } from 'react';
import cx from 'classnames';
import { Tooltip } from 'react-tooltip';

import { billingPeriod } from 'utilities/billing_period';
import { formatPrice } from 'utilities/money';

import { GreenButton, PurpleButton, WhiteButton } from '@marketing/components/buttons';
import { CurrentPlanProps, OfferProps } from '@marketing/types';
import { periodSuffix } from '@marketing/utilities/offer_price';

import ArrowWithLoop from './assets/arrow_with_loop.svg'
import CheckmarkIcon from './assets/checkmark_icon.svg';
import styles from './style.module.scss';

// A discount that only covers the first payment leaves later payments costing more
const showsRenewalPrice = (offer: OfferProps) => (
  offer.renewalPriceInCents != null && offer.renewalPriceInCents !== offer.priceInCents
);

// When the price only holds for the first payment, price it as the first period rather than as the
// ongoing rate
const priceSuffix = (offer: OfferProps) => (
  showsRenewalPrice(offer) ? ` / first ${billingPeriod(offer)}` : periodSuffix(offer)
);

// The student's own plan isn't something to buy again, so its button goes nowhere
const isCurrentPlan = (offer: OfferProps, props: Props) => (
  !!props.currentPlan && offer.slug === props.currentPlan.slug
);

// A student already on a plan they can change moves to a recurring offer rather than buying another,
// so its button says so. Every other button keeps its usual label.
const registrationLabel = (offer: OfferProps, props: Props, defaultLabel: string) => {
  if (isCurrentPlan(offer, props)) return 'Your Current Plan';

  return props.currentPlan?.canChangePlan && offer.recurring ? `Change Plan To ${offer.name}` : defaultLabel;
};

const registrationUrl = (offer: OfferProps, props: Props) => (
  isCurrentPlan(offer, props) ? undefined : offer.registrationUrl
);

interface PaidOffer {
  header: string | React.ReactNode;
  highlight?: string | React.ReactNode;
  offer: OfferProps;
  subHeader: string;
  valuePropGroups: {
    content: {
      content: string | React.ReactNode;
      icon?: string | React.ReactNode;
      tooltip?: string;
    }[];
    header: string;
    subHeader?: string;
  }[];
}

interface TrialOffer {
  header: string | React.ReactNode;
  offer: OfferProps;
  subHeader: string;
}

interface Props {
  currentPlan?: CurrentPlanProps | null;
  primaryOffer: PaidOffer;
  secondaryOffer: PaidOffer;
  tertiaryOffer?: PaidOffer;
  trialOffer: TrialOffer;
}

export default (props: Props) => {
  const prepareSubtextLine = (subtextLine, offer) => {
    let line = subtextLine;

    line = line.replace(
      '{{original-price}}',
      formatPrice(offer.fullPriceInCents, 'usd', true)
    );

    line = line.replace(
      '{{new-price}}',
      formatPrice(offer.priceInCents, 'usd', true)
    );

    return line;
  };

  const Feature = (props) => (
    <div
      className="d-flex gap-xs"
      data-tooltip-id="feature-tooltip"
      data-tooltip-html={props.tooltip}
    >
      {
        props.icon || (
          <img
            alt="Checkmark icon"
            height="16px"
            loading="lazy"
            src={CheckmarkIcon}
            style={{ height: 16, marginTop: 2, width: 14 }}
            width="14px"
          />
        )
      }

      <div className={cx(props.className, 'small text-inter')}>
        <span className={cx({ 'text-underdash': !!props.tooltip })}>
          {props.children}
        </span>
      </div>
    </div>
  );

  const OnSaleBadge = () => (
    <div
      className="align-items-center d-flex gap-xs"
      style={{
        background: 'linear-gradient(to bottom, rgb(253, 199, 66), rgb(252, 185, 20))',
        borderRadius: 4,
        fontSize: 11,
        padding: '4px 8px'
      }}
    >
      <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" fill="none">
        <path fill="#C38E0B" d="M12.24 6.278 6.722.76A.881.881 0 0 0 6.094.5H.944A.444.444 0 0 0 .5.944v5.15a.881.881 0 0 0 .26.628l5.518 5.518a.89.89 0 0 0 1.257 0l4.705-4.705a.89.89 0 0 0 0-1.257ZM3.389 4.056a.667.667 0 1 1 0-1.334.667.667 0 0 1 0 1.334Z" />
      </svg>

      <strong>ON SALE</strong>
    </div>
  );

  return (
    <div className={styles.offersContainer}>
      {/* Offsetting the target to make room for the sticky navbar and notice bar */}
      <div id="offers" style={{ marginTop: -140, position: 'absolute' }} />

      {
        props.currentPlan?.canChangePlan && (
          <div
            className="text-center text-inter u-margin-B-m"
            style={{
              background: '#F3EEF9',
              border: '1px solid #D9CCEB',
              borderRadius: 8,
              color: '#472375',
              padding: '12px 16px'
            }}
          >
            You&apos;re currently on the <strong>{props.currentPlan.name}</strong> plan. Select a
            different plan below.
          </div>
        )
      }

      {
        props.primaryOffer.highlight && (
          <div className="hidden-xs hidden-sm text-inter text-right u-padding-B-m u-padding-R-m" style={{ color: '#472375' }}>
            {props.primaryOffer.highlight}
          </div>
        )
      }

      <div
        className={cx(styles.pricingAndFeaturesContainer, 'd-flex flex-column gap-m')}
        style={{
          background: '#F7F7F7',
          border: '1px solid #DEDDDB',
          boxShadow: 'inset 0 2px 3px rgba(0, 0, 0, 0.1)'
        }}
      >
        <div className={cx(styles.offersGrid, 'd-flex flex-column flex-lg-row gap-l gap-lg-m text-inter')}>
          {
            props.tertiaryOffer && (
              <div className={cx(styles.offer, 'd-flex flex-column flex-1')}>
                <div className="d-flex flex-1 flex-column gap-m" style={{ background: '#FFFFFF' }}>
                  <div
                    className="d-flex flex-column gap-xs"
                    style={{ background: '#F7F7F7', borderRadius: 16, padding: 10 }}
                  >
                    <div style={{ fontSize: 18 }}>
                      {props.tertiaryOffer.header}
                    </div>

                    <div style={{ color: '#5C5C5C', fontSize: 13 }}>
                      {props.tertiaryOffer.subHeader}
                    </div>
                  </div>

                  <ExpandableContentAndFeatures
                    className="d-flex flex-column flex-1 gap-m"
                    triggerColor="#222123"
                  >
                    {
                      props.tertiaryOffer.valuePropGroups.map((group, groupIdx) => (
                        <div key={groupIdx} className={styles.offerContentAndFeatures}>
                          <div className="text-medium text-upper" style={{ color: '#5C5C5C', fontSize: 12 }}>
                            {group.header}
                          </div>

                          {
                            group.content.map((valueProp, idx) => (
                              <Feature key={idx} icon={valueProp.icon} tooltip={valueProp.tooltip}>
                                {valueProp.content}
                              </Feature>
                            ))
                          }
                        </div>
                      ))
                    }
                  </ExpandableContentAndFeatures>

                  <div className="d-flex flex-column gap-m justify-content-end">
                    <div className="d-flex flex-column flex-1 gap-xs">
                      {
                        props.tertiaryOffer.offer.priceInCents !== props.tertiaryOffer.offer.fullPriceInCents && (
                          <div className="d-flex gap-s">
                            <OnSaleBadge />

                            <strong
                              className="text-medium text-gray-darker"
                              style={{ fontSize: 15, textDecoration: 'line-through' }}
                            >
                              Usually {formatPrice(props.tertiaryOffer.offer.fullPriceInCents, 'usd', true)}{periodSuffix(props.tertiaryOffer.offer)}
                            </strong>
                          </div>
                        )
                      }

                      <div className="text-gray-darker" style={{ fontSize: 30 }}>
                        <strong>{formatPrice(props.tertiaryOffer.offer.priceInCents, 'usd', true)} USD{priceSuffix(props.tertiaryOffer.offer)}</strong>
                      </div>

                      {
                        showsRenewalPrice(props.tertiaryOffer.offer) && (
                          <div style={{ color: '#5C5C5C', fontSize: 14 }}>
                            Then <strong>{formatPrice(props.tertiaryOffer.offer.renewalPriceInCents, 'usd', true)} USD</strong>
                            {periodSuffix(props.tertiaryOffer.offer)}
                          </div>
                        )
                      }

                      {
                        props.tertiaryOffer.offer.subtextLines.length !== 0 && (
                          <div
                            className="d-flex flex-column"
                            style={{
                              borderTop: '1px solid rgba(0, 0, 0, 0.07)',
                              marginTop: 10,
                              padding: '10px 0',
                              width: '100%'
                            }}
                          >
                            {
                              props.tertiaryOffer.offer.subtextLines.map((subtextLine, idx) => (
                                <div key={idx} style={{ color: '#5C5C5C', fontSize: 14 }}>
                                  <strong>{prepareSubtextLine(subtextLine, props.tertiaryOffer.offer)}</strong>
                                </div>
                              ))
                            }
                          </div>
                        )
                      }

                      {
                        props.tertiaryOffer.offer.installmentsAvailable ? (
                          <div style={{ color: '#5C5C5C', fontSize: 14, height: 20 }}>
                            Or 4 monthly payments of {formatPrice(Math.round(props.tertiaryOffer.offer.priceInCents / 4))}
                          </div>
                        ) : (
                          /* Placeholder to match height of monthly installments div on other plans */
                          <div className="hidden-xs hidden-sm" style={{ height: 20 }} />
                        )
                      }
                    </div>

                    <PurchaseButtons
                      button={WhiteButton}
                      defaultLabel={registrationLabel(props.tertiaryOffer.offer, props, 'Sign Up Now')}
                      disabled={isCurrentPlan(props.tertiaryOffer.offer, props)}
                      offer={props.tertiaryOffer}
                    />
                  </div>
                </div>
              </div>
            )
          }

          <div className={cx(styles.offer, 'd-flex flex-column flex-1')}>
            <div className="d-flex flex-1 flex-column gap-m" style={{ background: '#E9F8F2' }}>
              <div
                className="d-flex flex-column gap-xs"
                style={{ background: '#C2EBDB', borderRadius: 16, padding: 10 }}
              >
                <div style={{ fontSize: 18 }}>
                  {props.secondaryOffer.header}
                </div>

                <div style={{ color: '#5C5C5C', fontSize: 13 }}>
                  {props.secondaryOffer.subHeader}
                </div>
              </div>

              <ExpandableContentAndFeatures
                className="d-flex flex-column flex-1 gap-m"
                triggerColor="#222123"
              >
                {
                  props.secondaryOffer.valuePropGroups.map((group, groupIdx) => (
                    <div key={groupIdx} className={styles.offerContentAndFeatures}>
                      <div className="text-medium text-upper" style={{ color: '#5C5C5C', fontSize: 12 }}>
                        {group.header}
                      </div>

                      {
                        group.content.map((valueProp, idx) => (
                          <Feature key={idx} icon={valueProp.icon} tooltip={valueProp.tooltip}>
                            {valueProp.content}
                          </Feature>
                        ))
                      }
                    </div>
                  ))
                }
              </ExpandableContentAndFeatures>

              <div className="d-flex flex-column gap-m justify-content-end">
                <div className="d-flex flex-column flex-1 gap-xs">
                  {
                    props.secondaryOffer.offer.priceInCents !== props.secondaryOffer.offer.fullPriceInCents && (
                      <div className="d-flex gap-s">
                        <OnSaleBadge />

                        <strong
                          className="text-medium text-gray-darker"
                          style={{ fontSize: 15, textDecoration: 'line-through' }}
                        >
                          Usually {formatPrice(props.secondaryOffer.offer.fullPriceInCents, 'usd', true)}{periodSuffix(props.secondaryOffer.offer)}
                        </strong>
                      </div>
                    )
                  }

                  <div className="text-gray-darker" style={{ fontSize: 30 }}>
                    <strong>{formatPrice(props.secondaryOffer.offer.priceInCents, 'usd', true)} USD{priceSuffix(props.secondaryOffer.offer)}</strong>
                  </div>

                  {
                    showsRenewalPrice(props.secondaryOffer.offer) && (
                      <div style={{ color: '#5C5C5C', fontSize: 14 }}>
                        Then <strong>{formatPrice(props.secondaryOffer.offer.renewalPriceInCents, 'usd', true)} USD</strong>
                        {periodSuffix(props.secondaryOffer.offer)}
                      </div>
                    )
                  }

                  {
                    props.secondaryOffer.offer.subtextLines.length !== 0 && (
                      <div
                        className="d-flex flex-column"
                        style={{
                          borderTop: '1px solid rgba(0, 0, 0, 0.07)',
                          marginTop: 10,
                          padding: '10px 0',
                          width: '100%'
                        }}
                      >
                        {
                          props.secondaryOffer.offer.subtextLines.map((subtextLine, idx) => (
                            <div key={idx} style={{ color: '#5C5C5C', fontSize: 14 }}>
                              <strong>{prepareSubtextLine(subtextLine, props.secondaryOffer.offer)}</strong>
                            </div>
                          ))
                        }
                      </div>
                    )
                  }

                  {
                    props.secondaryOffer.offer.installmentsAvailable ? (
                      <div style={{ color: '#5C5C5C', fontSize: 14, height: 20 }}>
                        Or 4 monthly payments of {formatPrice(Math.round(props.secondaryOffer.offer.priceInCents / 4))}
                      </div>
                    ) : (
                      /* Placeholder to match height of monthly installments div on other plans */
                      <div className="hidden-xs hidden-sm" style={{ height: 20 }} />
                    )
                  }
                </div>

                <PurchaseButtons
                  button={GreenButton}
                  className="text-gray-darker"
                  disabled={isCurrentPlan(props.secondaryOffer.offer, props)}
                  defaultLabel={registrationLabel(props.secondaryOffer.offer, props, 'Sign Up Now')}
                  offer={props.secondaryOffer}
                />
              </div>
            </div>
          </div>

          <div className="d-flex flex-column flex-1">
            {
              props.primaryOffer.highlight && (
                 <div className="text-inter text-center u-padding-B-s visible-xs visible-sm" style={{ color: '#472375' }}>
                  {props.primaryOffer.highlight}
                </div>
              )
            }

            <div className={cx(styles.offer, 'd-flex flex-column flex-1')} style={{ position: 'relative' }}>
              {
                props.primaryOffer.highlight && (
                  <div className={styles.arrowWithLoop}>
                    <img alt="" src={ArrowWithLoop} />
                  </div>
                )
              }

              <div className="d-flex flex-1 flex-column gap-m" style={{ background: '#472375' }}>
                <div
                  className="d-flex flex-column gap-xs"
                  style={{ background: '#5D3391', borderRadius: 16, padding: 10 }}
                >
                  <div style={{ fontSize: 18 }}>
                    {props.primaryOffer.header}
                  </div>

                  <div style={{ color: '#DDB2FF', fontSize: 13 }}>
                    {props.primaryOffer.subHeader}
                  </div>
                </div>

                <ExpandableContentAndFeatures
                  className="d-flex flex-column flex-1 gap-m"
                  triggerColor="#FFFFFF"
                >
                  {
                    props.primaryOffer.valuePropGroups.map((group, groupIdx) => (
                      <div key={groupIdx} className={styles.offerContentAndFeatures}>
                        <div className="text-medium text-upper" style={{ color: '#DDB2FF', fontSize: 12 }}>
                          {group.header}
                        </div>

                        {
                          group.subHeader && (
                            <div style={{ color: '#DDB2FF', fontSize: 14 }}>
                              {group.subHeader}
                            </div>
                          )
                        }

                        {
                          group.content.map((valueProp, idx) => (
                            <Feature key={idx} className="text-white" icon={valueProp.icon} tooltip={valueProp.tooltip}>
                              {valueProp.content}
                            </Feature>
                          ))
                        }
                      </div>
                    ))
                  }
                </ExpandableContentAndFeatures>

                <div className="d-flex flex-column gap-m justify-content-end">
                  <div className="d-flex flex-column flex-1 gap-xs">
                    {
                      props.primaryOffer.offer.priceInCents !== props.primaryOffer.offer.fullPriceInCents && (
                        <div className="d-flex gap-s">
                          <OnSaleBadge />

                          <strong
                            className="text-medium text-white"
                            style={{ fontSize: 15, textDecoration: 'line-through' }}
                          >
                            Usually {formatPrice(props.primaryOffer.offer.fullPriceInCents, 'usd', true)}{periodSuffix(props.primaryOffer.offer)}
                          </strong>
                        </div>
                      )
                    }
                    <div className="d-flex gap-s text-white" style={{ fontSize: 30 }}>
                      <strong>{formatPrice(props.primaryOffer.offer.priceInCents, 'usd', true)} USD{priceSuffix(props.primaryOffer.offer)}</strong>
                    </div>

                    {
                      showsRenewalPrice(props.primaryOffer.offer) && (
                        <div style={{ color: '#DDB2FF', fontSize: 14 }}>
                          Then <strong className="text-white">{formatPrice(props.primaryOffer.offer.renewalPriceInCents, 'usd', true)} USD</strong>
                          {periodSuffix(props.primaryOffer.offer)}
                        </div>
                      )
                    }

                    {
                      props.primaryOffer.offer.subtextLines.length !== 0 && (
                        <div
                          className="d-flex flex-column"
                          style={{
                            borderTop: '1px solid #DDB2FF',
                            marginTop: 10,
                            padding: '10px 0',
                            width: '100%'
                          }}
                        >
                          {
                            props.primaryOffer.offer.subtextLines.map((subtextLine, idx) => (
                              <div key={idx} style={{ color: '#FFFFFF', fontSize: 14 }}>
                                <strong>{prepareSubtextLine(subtextLine, props.primaryOffer.offer)}</strong>
                              </div>
                            ))
                          }
                        </div>
                      )
                    }

                    {
                      props.primaryOffer.offer.installmentsAvailable ? (
                        <div style={{ color: '#DDB2FF', fontSize: 14, height: 20 }}>
                          Or 4 monthly payments of {formatPrice(Math.round(props.primaryOffer.offer.priceInCents / 4))}
                        </div>
                      ) : (
                        /* Placeholder to match height of monthly installments div on other plans */
                        <div className="hidden-xs hidden-sm" style={{ height: 20 }} />
                      )
                    }
                  </div>

                  <PurchaseButtons
                    button={PurpleButton}
                    disabled={isCurrentPlan(props.primaryOffer.offer, props)}
                    defaultLabel={registrationLabel(props.primaryOffer.offer, props, 'Sign Up Now')}
                    offer={props.primaryOffer}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {
          // A student with a plan to change has no use for a free trial
          !props.currentPlan?.canChangePlan && (
            <div className="align-items-center d-flex flex-column gap-s u-margin-V-m">
              <div className="align-items-center d-flex flex-column text-inter" style={{ color: '#5C5C5C' }}>
                <div className="text-center"><strong>{props.trialOffer.header}</strong></div>
                <div className="text-center">{props.trialOffer.subHeader}</div>
              </div>

              <div>
                <PurchaseButtons
                  button={WhiteButton}
                  defaultLabel={registrationLabel(props.trialOffer.offer, props, 'Start a Free Trial')}
                  disabled={isCurrentPlan(props.trialOffer.offer, props)}
                  offer={props.trialOffer}
                  size="sm"
                />
              </div>
            </div>
          )
        }
      </div>

      <Tooltip className="text-center" id="feature-tooltip" />
    </div>
  );
};


// Renders one button per purchase option. An offer sold in a single flavor has one unlabeled
// option, so it renders the one button with this section's own copy.
const PurchaseButtons: React.FC<{
  button: React.FC<{
    children: React.ReactNode;
    className?: string;
    disabled?: boolean;
    href?: string;
    size?: null | 'sm';
  }>;
  className?: string;
  defaultLabel?: string;
  disabled?: boolean;
  offer: PaidOffer | TrialOffer;
  size?: null | 'sm';
}> = ({ button: Button, className, defaultLabel = 'Sign Up Now', disabled, offer, size }) => {
  return (
    <div className="d-flex flex-column gap-s">
      {
        offer.offer.purchaseOptions.map((purchaseOption, idx) => (
          <Button
            key={idx}
            className={className}
            disabled={disabled}
            href={purchaseOption.registrationUrl}
            size={size}
          >
            {purchaseOption.label || defaultLabel}
          </Button>
        ))
      }
    </div>
  );
};

const ExpandableContentAndFeatures: React.FC<{ children: any; className?: string; triggerColor: string }> = (props) => {
  const ref = useRef<HTMLDivElement>(null);
  const [contentHeight, setContentHeight] = useState(0);
  const [isExpanded, setIsExpanded] = useState(false);

  const determineContentHeight = useRef(() => {
    if (ref.current) {
      setContentHeight(ref.current.clientHeight);
    }
  });

  useEffect(() => {
    determineContentHeight.current();

    window.addEventListener('resize', determineContentHeight.current);

    return () => {
      window.removeEventListener('resize', determineContentHeight.current);
    };
  }, [props.children]);

  return (
    <>
      <div className="visible-xs visible-sm u-margin-B-s">
        <div
          onClick={() => setIsExpanded(!isExpanded)}
          style={{ cursor: 'pointer' }}
        >
          <span className="text-inter" style={{ color: props.triggerColor, fontSize: 14 }}>
            <strong>See plan details</strong>
          </span>

          <span style={{ paddingLeft: 5, transform: isExpanded ? 'rotate(180deg)': '' }}>
            <svg width="10" height="6" viewBox="0 0 10 6" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M0.75 1L4.0429 4.2929C4.4334 4.6834 5.0666 4.6834 5.4571 4.2929L8.75 1" stroke={props.triggerColor} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </span>
        </div>

        <div
          style={{
            height: isExpanded ? contentHeight : 0,
            overflow: 'hidden',
            transition: 'height 200ms'
          }}
        >
          <div className={props.className} ref={ref}>
            {props.children}
          </div>
        </div>
      </div>

      <div className="flex-1 hidden-xs hidden-sm">
        <div className={props.className}>
          {props.children}
        </div>
      </div>
    </>
  );
};
